import React, { useEffect, useRef, useState } from "react";
import { Rnd } from "react-rnd";
import ReactDOM from "react-dom";
import { Button, Popover } from "antd";
import OrderFilterSelect from "./OrderFilterSelect";
import ProductFilterTable from "./ProductFilterTable";
import { useLocalStorageState } from "ahooks";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import { observer } from "mobx-react-lite";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import { getProductFilterOrderItemStatus } from "./store/ProductFilterStore";

interface ProductFilterPanelProps {
    onOpenChange: (open: boolean) => void;
    showShopName?: boolean;
    tab: OrderTab;
}

const PRODUCT_FILTER_PANEL_SIZE = "PRODUCT_FILTER_PANEL_SIZE";
const PRODUCT_FILTER_PANEL_MIN_WIDTH = 380;
const DRAG_PREVIEW_ROW_THRESHOLD = 30;

function shouldUseLightweightDragPreview(panelElement: HTMLDivElement) {
    const contentElement = panelElement.querySelector<HTMLDivElement>(".content");
    const sourceTable = contentElement?.querySelector<HTMLTableElement>(":scope > table");
    const sourceBody = sourceTable?.tBodies[0];

    return !!sourceBody && sourceBody.rows.length > DRAG_PREVIEW_ROW_THRESHOLD;
}

const ProductFilterPanel: React.FC<ProductFilterPanelProps> = function (props) {
    const { onOpenChange, showShopName, tab } = props;
    const [btnElement, setBtnElement] = useState<HTMLElement>();
    const [loading, setLoading] = useState(false);
    const [size, setSize] = useLocalStorageState(PRODUCT_FILTER_PANEL_SIZE, {
        defaultValue: { width: 380, height: 740 },
    });
    const panelRef = useRef<HTMLDivElement>(null);
    const orderManagementStore = useOrderManagementStore();
    const { table } = useOrderManagementStore();

    useEffect(() => {
        try {
            table.productFilterStore.calcAggregatedOrderProducts(
                orderManagementStore.table.orders,
                getProductFilterOrderItemStatus(tab),
            );
        } catch (e) {
            console.log("error:", e);
        }
    }, [orderManagementStore.table.orders, table.productFilterStore.mergeRule, tab]);

    useEffect(() => {
        setBtnElement(document.getElementById("product-filter-btn"));

        const close = (evt: KeyboardEvent) => {
            if (evt.key === "Escape") {
                onOpenChange(false);
            }
        };

        document.addEventListener("keydown", close);

        return () => {
            document.removeEventListener("keydown", close);
        };
    }, []);

    if (!btnElement) return null;

    const { x = 0, y = 0 } = btnElement?.getBoundingClientRect() || {};

    return ReactDOM.createPortal(
        <Rnd
            dragHandleClassName="heading.move"
            enableUserSelectHack={false}
            enableResizing={true}
            bounds="body"
            style={{
                zIndex: 801,
                position: "fixed",
            }}
            minWidth={PRODUCT_FILTER_PANEL_MIN_WIDTH}
            minHeight={300}
            onDragStart={() => {
                if (!panelRef.current) return;

                if (shouldUseLightweightDragPreview(panelRef.current)) {
                    panelRef.current.classList.add("dragging");
                }
            }}
            onDragStop={() => {
                panelRef.current?.classList.remove("dragging");
            }}
            default={{
                x: x + (document.documentElement.scrollLeft || 0) - 309,
                y: y + (document.documentElement.scrollTop || 0) + 30,
                width: Math.max(size.width, PRODUCT_FILTER_PANEL_MIN_WIDTH),
                height: size.height,
            }}
            onResizeStop={(e, direction, ref, delta, position) => {
                setSize({
                    width: ref.offsetWidth,
                    height: ref.offsetHeight,
                });
            }}
        >
            <div ref={panelRef} className="product-filter-container">
                <div className="heading move tc title pr">
                    <Popover
                        content={
                            <span className="text-gray small">
                                支持过滤当前页单款多款的订单后，快速勾选当前页指定属性的订单
                            </span>
                        }
                    >
                        <span className="text-gray product-filter-dashed-tip">当前页面商品筛选</span>
                    </Popover>

                    <Button
                        type="link"
                        style={{ position: "absolute", right: 5, top: 1 }}
                        onClick={() => {
                            onOpenChange(false);
                        }}
                    >
                        收起
                    </Button>
                </div>
                <OrderFilterSelect setLoading={setLoading} loading={loading} />

                <ProductFilterTable tab={tab} showShopName={showShopName} size={size} />
            </div>
        </Rnd>,
        document.body,
    );
};

export default observer(ProductFilterPanel);
